/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { LEAD_COLORS, LeadStatusNameDashboard } from '@constants';
import { LeadChartData, LeadCluster, LeadLegend } from '@interfaces';
import {
  DASHBOARD_LEGENDS,
  DASHBOARD_MAP_OPTIONS,
  filterDashboardMapData,
  MAP_ID,
} from './constant';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'unicon-dashboard-map',
  templateUrl: './dashboard-map.component.html',
  styleUrls: ['./dashboard-map.component.scss'],
})
export class DashboardMapComponent implements OnInit, AfterViewInit {
  @ViewChild(GoogleMap) map!: GoogleMap;
  @Input() mapData?: LeadChartData[];
  protected mapId: string = MAP_ID;
  protected mapOptions: google.maps.MapOptions = DASHBOARD_MAP_OPTIONS;
  protected leadClusters: LeadCluster[] = [];
  protected currentZoom: number | undefined;
  protected leadColors: any = LEAD_COLORS;
  protected leadLegends: LeadLegend[] = DASHBOARD_LEGENDS;
  protected legends = this.leadLegends.filter((legend: LeadLegend) =>
    [
      LeadStatusNameDashboard.PAID,
      LeadStatusNameDashboard.QUOTED,
      LeadStatusNameDashboard.PROCESSING,
    ].includes(legend.name as LeadStatusNameDashboard),
  );
  private clusterer?: MarkerClusterer;
  private markers: google.maps.marker.AdvancedMarkerElement[] = [];

  async ngOnInit() {
    this.initializeMapOptions();
    this.mapId = environment.googleMaps.apiKey;
  }

  ngAfterViewInit() {
    if (this.map && this.map.googleMap) {
      this.map.googleMap.addListener('zoom_changed', () =>
        this.onZoomChanged(),
      );
      this.map.googleMap.addListener('bounds_changed', () =>
        this.updateClusters(),
      );
    }

    this.currentZoom = this.mapOptions.zoom ?? undefined;

    const waitForMapData = setInterval(() => {
      if (this.mapData && this.mapData.length > 0) {
        clearInterval(waitForMapData);
        if (this.map && this.map.googleMap) {
          google.maps.event.addListenerOnce(this.map.googleMap, 'idle', () => {
            this.updateClusters();
          });
        } else {
          console.warn('Google Map is not initialized');
        }
      }
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mapData'] && !changes['mapData'].firstChange) {
      this.updateClusters();
    }
  }

  protected onZoomChanged() {
    if (this.map) {
      this.currentZoom = this.map.getZoom();
      this.updateClusters();
    }
  }

  protected updateClusters() {
    if (!this.map.googleMap) return;

    const currentBounds = this.map?.googleMap?.getBounds();
    if (!currentBounds) return;

    const filterMapData = filterDashboardMapData(this.mapData ?? []);
    const visibleLeads = filterMapData.filter(lead =>
      currentBounds.contains(new google.maps.LatLng(lead.lat, lead.lng)),
    );

    // limpiar marcadores anteriores
    this.markers.forEach(marker => (marker.map = null));
    this.markers = [];

    // Crear AdvancedMarkers con pines negros y guardar el tipo en cada marker
    this.markers = visibleLeads.map(lead => {
      const pin = new google.maps.marker.PinElement({
        background: 'black',
        borderColor: 'black',
        glyphColor: 'black',
        scale: 0.5,
      });

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map.googleMap!,
        position: { lat: lead.lat, lng: lead.lng },
        content: pin.element,
      }) as any;

      marker.leadType = lead.type;
      return marker;
    });

    // inicializar o actualizar clusterer
    if (!this.clusterer) {
      this.clusterer = new MarkerClusterer({
        map: this.map?.googleMap,
        markers: this.markers,
        renderer: {
          render: ({ count, position, markers }) => {
            // Contar cuántos hay de cada tipo
            const typeCount: Record<string, number> = {};
            (markers as any[]).forEach(m => {
              const t = m.leadType;
              typeCount[t] = (typeCount[t] || 0) + 1;
            });

            // Encontrar el tipo predominante
            let predominantType = Object.keys(typeCount)[0];
            let maxCount = typeCount[predominantType];
            for (const t in typeCount) {
              if (typeCount[t] > maxCount) {
                predominantType = t;
                maxCount = typeCount[t];
              }
            }

            const color = this.leadColors[predominantType] || '#666666';

            // Crear marker personalizado
            const div = document.createElement('div');
            div.style.width = '40px';
            div.style.height = '40px';
            div.style.borderRadius = '50%';
            div.style.backgroundColor = color;
            div.style.display = 'flex';
            div.style.justifyContent = 'center';
            div.style.alignItems = 'center';
            div.style.color = 'white';
            div.style.fontWeight = 'bold';
            div.textContent = String(count);

            return new google.maps.marker.AdvancedMarkerElement({
              position,
              content: div,
            });
          },
        },
      });
    } else {
      this.clusterer.clearMarkers();
      this.clusterer.addMarkers(this.markers);
    }

    // Actualizar leadClusters si aún necesitas círculos
    this.leadClusters = visibleLeads.map(lead => ({
      position: { lat: lead.lat, lng: lead.lng },
      radius: 200,
      type: lead.type,
      count: 1,
      color: this.leadColors[lead.type],
    }));
  }

  protected getCircleOptions(cluster: any): google.maps.CircleOptions {
    const color = this.leadColors[cluster.type];
    const zoomFactor = this.currentZoom ? 20 - this.currentZoom : 10;
    return {
      center: cluster.position,
      radius: Math.sqrt(cluster.count) * zoomFactor * 30, // escala dinámica según cantidad
      fillColor: color,
      fillOpacity: 1,
      strokeColor: color,
      strokeOpacity: 1,
      strokeWeight: 2,
    };
  }

  protected trackByClusterId(index: number, cluster: LeadCluster): string {
    return (
      cluster.position.lat +
      '-' +
      cluster.position.lng +
      '-' +
      cluster.type +
      '-' +
      index
    );
  }

  private initializeMapOptions(): void {
    this.mapOptions = {
      ...this.mapOptions,
      mapId: this.mapId,
    };
  }
}
